import React from 'react';
import { Box, Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { Section, ResponsiveContainer } from '@lib/common';

export const TermsPage: React.FC = () => {
  const { t } = useTranslation();

  const sectionProps = {
    title: t('terms.title'),
  };

  return (
    <ResponsiveContainer>
      <Section {...sectionProps}>
        <Box paddingBottom={2}>
          <Typography>
            PLEASE READ THESE TERMS AND CONDITIONS CAREFULLY BEFORE MAKING A RESERVATION AND STAYING
            AT OUR PROPERTIES. THIS IS A BINDING LEGAL AGREEMENT AND YOU AGREE TO EACH AND EVERY ONE
            OF THESE TERMS AND CONDITIONS IF YOU CHOOSE TO CONTINUE.
          </Typography>
        </Box>
        <Box paddingBottom={2}>
          <Typography>
            This Agreement is by and between Armadillo Hotel Group, LLC and its wholly owned
            subsidiaries including AHG Management, LLC and AHG Technologies, LP (referred to herein
            as “Armadillo", "we", "us", or "our") and guest and guest's employer, in the event
            guest's employer has reserved the room on guest's behalf (referred to herein as “you”
            or “your").
          </Typography>
        </Box>
        <Box paddingBottom={2}>
          <Typography variant="h5">MAKING, MODIFYING AND CANCELLING RESERVATIONS</Typography>
        </Box>
        <Box paddingBottom={2}>
          <Typography>
            Reservations may be made directly with our hotels through our company website
            (www.armadillohotel.com) or through an online booking service (expedia.com, booking.com
            etc.). Non-refundable reservations cannot be changed or cancelled for any reason.
            Flexible reservations may be changed or cancelled prior to 12:00 AM the day before
            check-in (the "Cut-Off Time”).  For example, a reservation that begins on Tuesday can be
            dropped without charge up until 11:59PM on Monday. Similarly, a checked-in reservation
            or no show reservation seeking to drop remaining days can be done without charge if done
            prior to 11:59PM (or one additional day will be charged).  Flexible reservations made
            directly with one of our hotels may only be modified or cancelled prior to the Cut-Off
            Time through the "My Stays" tab on our website. Flexible reservations made through an
            online booking service may only be modified or cancelled by contacting the original
            booking service. All requests for reservations or extensions are subject to availability
            and may be refused for any reason.  No show reservations will continue to be held for
            two days and then automatically cancelled.
          </Typography>
        </Box>
        <Box paddingBottom={2}>
          <Typography variant="h5">CHECK-IN/CHECK-OUT</Typography>
        </Box>
        <Box paddingBottom={2}>
          <Typography>
            Guests may check-in AFTER 03:00 PM (local time) on the stated arrival/check-in date. All
            guests will receive email and text notifications when their check-in is available with a
            link which must be clicked to complete the room assignment. Once the room assignment is
            complete, a QR code which will serve as the guest room key will be emailed and texted to
            the guest. The QR code can also be found anytime through the "My Key" tab on our
            website. Early check-in is not guaranteed but can be requested through the "My Stays"
            tab on our website for an additional fee.
          </Typography>
        </Box>
        <Box paddingBottom={2}>
          <Typography>
            Guests must check-out BEFORE 11:00 AM on the stated departure/check-out date. Access to
            the guest room will be automatically revoked after the check-out time. Failure to vacate
            the room by the check-out time may result in additional hold-over charges. Late
            check-out is not guaranteed but can be requested through the "My Stays" tab on our
            website for an additional fee.
          </Typography>
        </Box>
        <Box paddingBottom={2}>
          <Typography variant="h5">HOTEL POLICIES</Typography>{' '}
        </Box>
        <Typography>
          By booking a room at one of our hotels, guest and guest’s employer hereby agrees to the
          following hotel policies and other rules established by AHG and not to permit any of their
          employees, agents, subcontractors, licensees, invitees and guests to use the property for
          any use or purpose in violation of any federal, state or local laws:
        </Typography>
        <ul>
          <li>
            <Typography>
              Meals (if included in room rate) will be served from 3:00-8:00 AM and from 3:00- 8:00
              PM with bag lunches available for pickup during the morning and evening meals
            </Typography>
          </li>
          <li>
            <Typography>
              Housekeeping will be performed as follows: touch-up clean mid-week, full clean (sheet
              change) weekly and full turnaround cleaning upon checkout.
            </Typography>
          </li>
          <li>
            <Typography>
              Maximum occupancy is limited to no more than two persons per room. At least one
              registered adult guest (18+) is required per room.
            </Typography>
          </li>
          <li>
            <Typography>All overnight guests must be registered.</Typography>
          </li>
          <li>
            <Typography>No pets allowed (other than legitimate service animals).</Typography>
          </li>
          <li>
            <Typography>
              No firearms or other weapons are allowed on premises for any reason, regardless of
              whether guest possesses concealed or open carry license.
            </Typography>
          </li>
          <li>
            <Typography>No illegal drugs are permitted on premises for any reason.</Typography>
          </li>
          <li>
            <Typography>
              No alcoholic beverages are permitted on premises unless served by the hotel and
              consumed in designated areas.
            </Typography>
          </li>
          <li>
            <Typography>
              Each room is furnished with lockable storage drawers/cabinets. The hotel is not
              responsible for any loss of personal items left in rooms.
            </Typography>
          </li>
          <li>
            <Typography>
              The hotel is not responsible for property damage or personal injuries (including,
              without limitation, accidental death) due to vehicular accidents caused by guests.
            </Typography>
          </li>
          <li>
            <Typography>
              The hotel is not responsible for any personal injuries (including, without limitation,
              accidental death) incurred while using the gym/fitness center facilities, where
              available. If you use the gym/fitness center facilities, you do so at your own risk.
            </Typography>
          </li>
          <li>
            <Typography>
              The hotel utilizes video surveillance cameras throughout the property including,
              without limitation, the parking areas and grounds, the administration building and the
              breezeways and corridors leading to the guest rooms. Surveillance video and images may
              be retained indefinitely.
            </Typography>
          </li>
        </ul>
        <Box paddingBottom={2}>
          <Typography variant="h5">GUEST RESPONSIBILITIES</Typography>
        </Box>
        <Typography>
          Armadillo in its sole and absolute discretion may remove a guest immediately from the
          property without refund and take other appropriate action for any the following actions:
        </Typography>
        <ul>
          <li>
            <Typography>Violating any of the foregoing hotel policies</Typography>
          </li>
          <li>
            <Typography>
              Abusive treatment of hotel staff or refusal to follow instructions of hotel staff or
              security personnel
            </Typography>
          </li>
          <li>
            <Typography>
              Being involved in any police matter unless the matter arises through no fault of your
              own or due to circumstances beyond your control
            </Typography>
          </li>
          <li>
            <Typography>
              Tampering with or removing window stops, smoke detectors or other safety measures
            </Typography>
          </li>
          <li>
            <Typography>Leaving room or common area windows or doors unlocked or open</Typography>
          </li>
          <li>
            <Typography>Soliciting or loitering of any kind, anywhere on the property</Typography>
          </li>
          <li>
            <Typography>Parking in unauthorized areas</Typography>
          </li>
          <li>
            <Typography>
              Parking utility trailers, oversized vehicles or inoperative vehicles on the property
              without authorization
            </Typography>
          </li>
          <li>
            <Typography>
              Washing, repairing, modifying, advertising the sale of or storing vehicles on the
              property
            </Typography>
          </li>
          <li>
            <Typography>
              Being outside your room without being fully clothed, including shirt and shoes
            </Typography>
          </li>
          <li>
            <Typography>
              Cooking, grilling or barbecuing in unauthorized areas or creating fire hazards
              on-premises
            </Typography>
          </li>
          <li>
            <Typography>
              Displaying items including signage, lighting, clothing, etc., outside your room
            </Typography>
          </li>
          <li>
            <Typography>
              Leaving minors unattended anywhere on property, including in your room
            </Typography>
          </li>
          <li>
            <Typography>
              Causing disruptions, including the use of car horns, in the parking lot
            </Typography>
          </li>
          <li>
            <Typography>
              Damaging or destroying hotel property and facilities anywhere on the property
            </Typography>
          </li>
          <li>
            <Typography>
              Disrupting other guests with loud noise, TV, music, disorderly conduct or large groups
            </Typography>
          </li>
          <li>
            <Typography>
              Feeding stray animals on the property or keeping animals inside parked vehicles
            </Typography>
          </li>
          <li>
            <Typography>
              Leaving trash or personal items in common spaces or unauthorized areas
            </Typography>
          </li>
          <li>
            <Typography>
              Using skates, skateboards, bikes, motorcycles, ATVs or other recreational vehicles or
              devices anywhere on the property
            </Typography>
          </li>
          <li>
            <Typography>
              Creating or staying in unsanitary conditions (trash, odors, dirty dishes, filth, etc.)
            </Typography>
          </li>
          <li>
            <Typography>
              Using the "Armadillo Hotel" name and/or contact information for any personal or
              commercial purpose, including social media or other internet posts
            </Typography>
          </li>
          <li>
            <Typography>
              Engaging in any activity, including social media or other internet posts that might
              jeopardize the safety and privacy of any Armadillo guest or employee or cast Armadillo
              in a false or disparaging light
            </Typography>
          </li>
        </ul>
        <Box paddingBottom={2}>
          <Typography variant="h5">OTHER TERMS AND CONDITIONS</Typography>
        </Box>
        <Box paddingBottom={2}>
          <Typography>
            Guest and guest’s employer will be responsible for maintaining adequate insurance in
            connection with this Agreement and agree that all coverages (including workers’ comp)
            will be primary and non-contributory coverage and no "other insurance" clause may be
            invoked by any insurer to the extent of liabilities assumed in this Agreement.  Guest
            and guest’s employer shall have their insurer(s) waive their right of subrogation
            against Armadillo on all insurance carried to the extent of liabilities assumed in this
            Agreement. Guest and guest’s employer shall immediately notify its underwriters and
            Armadillo, and shall furnish all necessary information concerning, any occurrence which
            may give rise to a claim. Guest and guest’s employer shall require all claims by its
            personnel to be submitted first to its workers’ compensation insurance carrier (to the
            extent worker’s compensation is applicable to the claim), even though such loss or
            damage might have been due to the negligence, gross negligence or willful misconduct of
            Armadillo or its personnel. Unless expressly stated to the contrary elsewhere in this
            Agreement or prohibited by applicable law or legal statute, guest's and guest’s
            employer's indemnification obligations under this Agreement shall not be limited by
            amount or in scope to coverage provided by insurance which is required under this
            Agreement.
            <b>
              &nbsp;THIS PARAGRAPH IS INTENDED TO WAIVE CLAIMS EVEN IN THE EVENT OF ARMADILLO'S
              OWN NEGLIGENCE.
            </b>
          </Typography>
        </Box>
        <Box paddingBottom={2}>
          <Typography>
            Guest and guest’s employer hereby notify Armadillo, pursuant to Texas Administrative
            Code Section 3.161(b)(6)(A)(i), of the intent to use or occupy a room or space within
            each of Armadillo Hotel’s Texas locations for the next 30 or more consecutive days.
            Occupancy tax will be due from the first day of the stay for any rooms not occupied for
            30 or more consecutive days.
          </Typography>
        </Box>
        <Box paddingBottom={2}>
          <Typography>
            All sums shall be paid at the times and in the amounts provided for herein in legal
            tender of the United States of America and without notice, demand, abatement, deduction
            or offset. Guest and guest’s employer will be solely responsible for inputting into the
            online portal at the time of booking any additional information required (such as cost
            center, employee number, crew name etc.) in order for Armadillo to receive payment. All
            invoices will be net 30-day terms.  Any past due accounts shall incur finance charges
            equal to 1 1/2% per month from the date the account became past due until paid in full.
          </Typography>
        </Box>
        <Box paddingBottom={2}>
          <Typography>
            Guest and guest’s employer acknowledge that Armadillo is the owner of all intellectual
            property currently utilized or hereinafter created related to the property management
            system (the "Intellectual Property") and guest and guest’s employer hereby assign to
            Armadillo any and all right, title and interest guest or guest’s employer may have in
            and to the Intellectual Property, including all copyright, publishing rights, moral
            rights and all rights to use, reproduce, and otherwise exploit the Intellectual Property
            in any and all formats or media and all channels, whether known or hereafter created.
             Armadillo has developed proprietary systems to seek to minimize any applicable
            occupancy taxes related to the services provided hereto and shall retain as additional
            compensation any such tax exemptions prepared by Armadillo and claimed for guest or
            guest’s employer, unless a lesser amount is agreed to in writing. In addition, Armadillo
            shall retain as additional compensation and as a setoff for collection costs all of the
            tax exemptions for any past due account.  
          </Typography>
        </Box>
        <Box paddingBottom={2}>
          <Typography>
            GUEST AND GUEST’S EMPLOYER AGREE TO DEFEND, RELEASE, INDEMNIFY AND HOLD HARMLESS
            ARMADILLO AND THE AFFILIATES, OWNERS, OFFICERS, DIRECTORS, EMPLOYEES, AGENTS AND
            REPRESENTATIVES OF ANY OF THE FOREGOING (THE “INDEMNIFIED PARTIES”) FROM AND AGAINST ANY
            AND ALL LOSSES, DAMAGES, EXPENSES OR LIABILITIES OF ANY KIND INCLUDING REASONABLE
            ATTORNEYS’ FEES (“LIABILITIES”) CAUSED BY, ARISING FROM OR OUT OF, ATTRIBUTABLE TO OR IN
            CONNECTION WITH (A) ACTS OR OMISSIONS OF GUEST AND GUEST’S EMPLOYER WHICH CONSTITUTE
            NEGLIGENCE, GROSS NEGLIGENCE OR WILLFUL MISCONDUCT, (B) THE BREACH BY GUEST OR GUEST’S
            EMPLOYER OF ANY OF ITS OBLIGATIONS CONTAINED IN THESE TERMS AND CONDITIONS, (C) THE
            BODILY INJURY OR DEATH OF GUEST OR ANY PERSONNEL OF GUEST’S EMPLOYER, AND (D) ANY
            OCCUPANCY OR SALES TAXES (OR INTEREST AND PENALTIES RELATED TO DISALLOWANCE OF ANY
            CLAIMED EXEMPTIONS) RELATED TO THE SERVICES PROVIDED HERETO. IT IS THE INTENTION OF THE
            PARTIES THAT THE FOREGOING INDEMNIFICATION OBLIGATIONS APPLY TO ALL LIABILITIES OF EVERY
            KIND AND NATURE, WHETHER ARISING UNDER THE LAW OF CONTRACTS, PROPERTY OR TORTS, OR BY
            STATUTE, AT COMMON LAW OR IN EQUITY AND REGARDLESS OF THE CONTRIBUTORY NEGLIGENCE OF
            ARMADILLO.
          </Typography>
        </Box>
        <Box paddingBottom={2}>
          <Typography>
            THIS CONTRACT IS MADE AND PERFORMED IN SUBSTANTIAL PART IN AUSTIN, TX. THE TERMS AND
            CONDITIONS HEREOF SHALL BE CONSTRUED IN ACCORDANCE WITH AND GOVERNED BY THE LAWS OF THE
            STATE OF TEXAS, WITHOUT REFERENCE TO THE CONFLICT OF LAWS PRINCIPLES THEREOF. IN THE
            EVENT OF ANY DISPUTE RELATED TO THE SERVICES PROVIDED BY ARMADILLO, THE PARTIES AGREE TO
            SUBMIT TO THE EXCLUSIVE JURISDICTION OF THE FEDERAL AND STATE COURTS SITTING IN TRAVIS
            COUNTY, TEXAS AND HEREBY WAIVE ANY RIGHT TO TRIAL BY JURY. ARMADILLO MAY, IN ITS SOLE
            AND ABSOLUTE DISCRETION, ELECT TO REFER ANY CONTROVERSY OR CLAIM TO BINDING ARBITRATION
            ADMINISTERED BY THE AMERICAN ARBITRATION ASSOCIATION IN FRONT OF A SINGLE ARBITRATOR
            LOCATED IN AUSTIN, TX. THE PARTIES AGREE THAT ANY PRIOR FILED COURT ACTIONS SHALL BE
            DISMISSED AND JUDGEMENT ON THE AWARD RENDERED BY THE ARBITRATOR MAY BE ENTERED IN ANY
            COURT HAVING JURISDICTION THEREOF.
          </Typography>
        </Box>
        <Box paddingBottom={2}>
          <Typography>
            In no event, whether as a result of termination or otherwise, shall Armadillo be liable
            to guest or guest's employer for any special, indirect or consequential damages, or
            damages for loss of opportunity, loss of profit, increased operating cost or costs
            associated with any business interruption, or any other similar damages of any nature or
            kind.
          </Typography>
        </Box>
        <Box paddingBottom={2}>
          <Typography>
            Any notice or communication to Armadillo shall be given only by overnight delivery
            service or certified mail (return receipt requested), postage paid and addressed to the
            address specified below or to such other address as may be substituted by proper notice.
             Any such notice shall conclusively be deemed delivered when delivery is indicated on
            the receipt or other indicia of delivery by the overnight courier service or the United
            States Postal Service (in the case of delivery by certified mail, return receipt
            requested).
          </Typography>
        </Box>
        <Box paddingBottom={2}>
          <Typography>
            Armadillo Hotel Group, LLC
            <br />
            9430 Research Blvd., Bldg. 4 Suite 120
            <br />
            Austin, TX 78759
            <br />
            Attn: General Counsel
          </Typography>
        </Box>
        <Box paddingBottom={2}>
          <Typography>
            This Agreement shall be binding upon the parties hereto, their heirs, devisees, personal
            representatives, successors and assigns, from and after the date hereof.
          </Typography>
        </Box>
        <Box paddingBottom={2}>
          <Typography>
            Each Party agrees that neither it nor any of its affiliates will offer or provide money,
            gifts, or any other things of value directly or indirectly to anyone in order to
            improperly influence any act or decision contemplated by this Agreement, including by
            assisting any party to secure an unlawful advantage. This Agreement constitutes the
            entire agreement of the Parties hereto with respect to, and supersedes all prior written
            and oral agreements, understandings and negotiations with respect to, the subject matter
            hereof.
          </Typography>
        </Box>
        <Box paddingBottom={2}>
          <Typography>
            If any provision of this Agreement should be held to be invalid or unenforceable, the
            validity and enforceability of the remaining provisions of this Agreement shall not be
            affected thereby.
          </Typography>
        </Box>
        <Typography style={{ fontWeight: 2 }}>
          THE FOREGOING RULES, RESTRICTIONS, TERMS AND CONDITIONS AND HOTEL POLICIES AND GUEST
          RESPONSIBILITIES MAY BE MODIFIED FROM TIME TO TIME IN OUR SOLE AND ABSOLUTE DISCRETION
          WITHOUT PRIOR NOTICE TO GUEST OR GUEST’S EMPLOYER
        </Typography>
      </Section>
    </ResponsiveContainer>
  );
};
